import React, { useContext, useCallback, useEffect, useMemo } from 'react';
import { Grid, makeStyles, Theme } from '@material-ui/core';
import clsx from 'clsx';
import { ApiError, DownloadFileRequest, FileRequest, FileRequestAction, FileRequestStatus, isDownloadFileRequest, RobotCloudAPI }
    from '@moderepo/mode-apis';
import { modeUIContext, showSuccessNotification, showDialog, DialogType, DialogResponse, DialogResponseCode, hideSlideOutPanel,
    showErrorNotification } from '@moderepo/mode-ui-state';
import { selectProjectById, ritzDataContext } from '@moderepo/mode-data-state-ritz';
import { capitalizeAll, BaseCompAction, SlideOutComp, getErrorMessage } from '@moderepo/mode-app-components';
import { cancelPendingFileRequest, deleteFileRequest, fetchFileRequestById, rcDataContext, selectFileRequestById }
    from '@moderepo/mode-data-state-robot-cloud';
import { useRitzTranslation } from '../../../utils/customHooks';
import { RCFileRequestInfoContainer } from './RCFileRequestInfoContainer';



const useStyle = makeStyles((theme: Theme) => {
    return {
        primaryAction: {
            '&.download': {
                background: theme.palette.success.main,
                color     : theme.palette.success.contrastText,
            },
        },

        secondaryAction: {
            '&:not(.unusable)': {
                '&.remove, &.cancel': {
                    '& .icon': {
                        color: theme.palette.secondary.main,
                    },
                },
            },
        },
    };
});



// These are the request statuses which the request CAN be canceled
const cancelableStatuses = [
    FileRequestStatus.PENDING, FileRequestStatus.RECEIVING,
];


// These are the request statuses which the request CANNOT be deleted
const notDeleteableStatuses = [
    FileRequestStatus.RECEIVING, FileRequestStatus.PENDING,
];



interface RCFileRequestPreviewContainerProps {
    readonly projectId: number;
    readonly homeId: number;
    readonly deviceId: number;
    readonly robotId?: string | undefined;

    // This can be either a number or a FileRequestAction. To show a preview of an existing file request, pass the fileRequest's ID to this
    // prop. To create a new file request, pass a FileRequestAction to this prop.
    readonly selectedFileRequest: number | FileRequestAction;

    // these are OPTIONAL and only available if the user is on a User, Home, or Device page
    readonly context?: {
        readonly userId?: number | undefined;
        readonly homeId?: number | undefined;
        readonly deviceId?: number | undefined;
    }
}


/**
 * This page is used for showing preview of a File Request.
 */
export const RCFileRequestPreviewContainer: React.FC<RCFileRequestPreviewContainerProps> = (props: RCFileRequestPreviewContainerProps) => {

    const { projectId } = props;
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState } = useContext(ritzDataContext);
    const { state:rcDataState, dispatch:rcDataDispatch } = useContext(rcDataContext);
    const project = selectProjectById(dataState, projectId);
    const selectedFileRequest = typeof props.selectedFileRequest === 'number'
        ? selectFileRequestById(rcDataState, props.homeId, props.selectedFileRequest) : undefined;

    // If selectedFileRequest is NOT a number then we are in a Create File Request mode
    const isNewFileRequest = typeof props.selectedFileRequest !== 'number';

    const { trans } = useRitzTranslation(project);
    const classes = useStyle();



    /**
     * Hide the slide out panel
     */
    const onHideSlideOutPanel = useCallback(() => {
        uiDispatch(hideSlideOutPanel());
    }, [uiDispatch]);



    // This useEffect is used for loading file request data for the selected file request if we don't have the data yet
    useEffect(() => {
        if (typeof props.selectedFileRequest === 'number' && !selectedFileRequest) {
            rcDataDispatch(fetchFileRequestById(props.homeId, props.selectedFileRequest)).catch(() => {
                // ignore error because dataDispatch already handled it
            });
        }
    }, [props.selectedFileRequest, projectId, selectedFileRequest, rcDataDispatch, props.homeId]);



    /**
     * On Cancel File Request action clicked
     */
    const onCancelFileRequestActionClicked = useCallback((fileRequest: FileRequest) => {

        // If the request is not cancelable then show an error message to let the user know
        if (!cancelableStatuses.includes(fileRequest.status)) {
            uiDispatch(showErrorNotification('file_transfer.notif.error.blocking_cancel'));
            return;
        }

        // Confirm before canceling request
        uiDispatch(showDialog({
            type          : DialogType.CONFIRM,
            title         : 'dialog.generic_confirm_cancel.title',
            titleData     : { object_term: capitalizeAll(trans('label.file_request')), object_name: fileRequest.id },
            message       : 'dialog.generic_confirm_cancel.message',
            messageData   : { object_term: capitalizeAll(trans('label.file_request')), object_name: fileRequest.id },
            positiveButton: 'label.yes',
            negativeButton: 'label.no',
            onClose       : async (response?: DialogResponse): Promise<void> => {
                if (response && response.code === DialogResponseCode.POSITIVE) {
                    rcDataDispatch(cancelPendingFileRequest(props.homeId, fileRequest.id)).then(() => {
                        uiDispatch(showSuccessNotification('notif.generic_item_canceled_successfully', { term: 'label.file_request' }));

                        // File request deleted, clear the selected entity
                        onHideSlideOutPanel();
                    }).catch(() => {
                        // ignore error
                    });
                }
            },
        }));
    }, [uiDispatch, trans, rcDataDispatch, props.homeId, onHideSlideOutPanel]);


    /**
     * On Delete File Request action clicked
     */
    const onRemoveFileRequestActionClicked = useCallback((fileRequest: FileRequest) => {

        // If the request is not deletable then show an error message to let the user know
        if (notDeleteableStatuses.includes(fileRequest.status)) {
            uiDispatch(showErrorNotification('file_transfer.notif.error.blocking_delete'));
            return;
        }

        // Confirm before deleting request
        uiDispatch(showDialog({
            type          : DialogType.CONFIRM,
            title         : 'dialog.generic_confirm_remove.title',
            titleData     : { object_term: capitalizeAll(trans('label.file_request')), object_name: fileRequest.id },
            message       : 'dialog.generic_confirm_remove.message',
            messageData   : { object_term: capitalizeAll(trans('label.file_request')), object_name: fileRequest.id },
            positiveButton: 'label.delete',
            negativeButton: 'label.cancel',
            onClose       : async (response?: DialogResponse): Promise<void> => {
                if (response && response.code === DialogResponseCode.POSITIVE) {
                    rcDataDispatch(deleteFileRequest(props.homeId, fileRequest.id)).then(() => {
                        uiDispatch(showSuccessNotification('notif.generic_item_deleted_successfully', { term: 'label.file_request' }));

                        // File request deleted, clear the selected entity
                        onHideSlideOutPanel();
                    }).catch(() => {
                        // ignore error
                    });
                }
            },
        }));
    }, [uiDispatch, trans, rcDataDispatch, props.homeId, onHideSlideOutPanel]);



    /**
     * On "Reload" button click, make an API call to reload the data for the selected request
     */
    const onReloadFileRequestActionClicked = useCallback(async (fileRequest: FileRequest) => {
        rcDataDispatch(fetchFileRequestById(fileRequest.homeId, fileRequest.id)).catch(() => {
            // ignore error because rcDataDispatch already handled the error
        });
    }, [rcDataDispatch]);



    /**
     * On 'Download' button click, we will make an API call to get the download URL and then open it in a new tab
     */
    const onDownloadFileRequestActionClicked = useCallback(async (fileRequest: DownloadFileRequest) => {
        try {
            const downloadUrl = await RobotCloudAPI.getInstance().getDownloadFileRequestDownloadURL(fileRequest.homeId, fileRequest.id);
            window.open(downloadUrl, 'blank');
        } catch (error) {
            uiDispatch(showErrorNotification(getErrorMessage((error as ApiError).code)));
        }
    }, [uiDispatch]);



    /**
     * Create a list of primary actions to be displayed in the SlideOutPanel action bar
     */
    const primaryActions = useMemo(() => {
        if (!isNewFileRequest && selectedFileRequest) {
            // This is the list of statuses that indicate the request IS COMPLETED and the status is FINAL and will not change anymore.
            // If a request has one of these statuses, we will NOT allow the user to reload the request because there is no point of reloading it.
            const notReloadableStatuses = [
                FileRequestStatus.CANCELED,
                FileRequestStatus.FAILED,
                FileRequestStatus.SUCCEEDED,
                FileRequestStatus.TIME_OUT,
                FileRequestStatus.WARNING,
                FileRequestStatus.NOT_FOUND,
            ];


            const actions: BaseCompAction[] = [];

            if (isDownloadFileRequest(selectedFileRequest) && selectedFileRequest.status === FileRequestStatus.SUCCEEDED) {
                // if it is a SUCCESS download request, show the download button
                actions.push({
                    className: clsx(classes.primaryAction, 'download'),
                    icon     : 'get_app',
                    label    : capitalizeAll(trans('file_transfer.label.download_file_request')),
                    onClick  : () => {
                        onDownloadFileRequestActionClicked(selectedFileRequest);
                    },
                });
            }

            if (!notReloadableStatuses.includes(selectedFileRequest.status)) {
                // if the request's status one of the status which can still be changed then we will show the 'Reload' button so the user
                // can trigger to reload the request data
                actions.push({
                    className: clsx(classes.primaryAction, 'reload'),
                    icon     : 'sync',
                    label    : capitalizeAll(trans('label.reload')),
                    onClick  : () => {
                        onReloadFileRequestActionClicked(selectedFileRequest);
                    },
                });
            }

            return actions;
        }

        return undefined;
    }, [classes.primaryAction, isNewFileRequest, onDownloadFileRequestActionClicked, onReloadFileRequestActionClicked, selectedFileRequest, trans]);


    /**
     * Create a list of secondary actions to be displayed in the SlideOutPanel dropdown menu
     */
    const secondaryActions = useMemo(() => {

        if (!isNewFileRequest && selectedFileRequest) {
            const actions: BaseCompAction[] = [];

            actions.push({
                className: clsx(classes.secondaryAction, 'cancel', !cancelableStatuses.includes(selectedFileRequest.status) && 'unusable'),
                icon     : 'cancel',
                label    : capitalizeAll(trans('file_transfer.label.cancel_file_request')),
                onClick  : () => {
                    onCancelFileRequestActionClicked(selectedFileRequest);
                },
            });

            actions.push({
                className: clsx(classes.secondaryAction, 'remove', notDeleteableStatuses.includes(selectedFileRequest.status) && 'unusable'),
                icon     : 'delete',
                label    : capitalizeAll(trans('file_transfer.label.delete_file_request')),
                onClick  : () => {
                    onRemoveFileRequestActionClicked(selectedFileRequest);
                },
            });

            return actions;
        }

        return undefined;
    }, [classes.secondaryAction, isNewFileRequest, onCancelFileRequestActionClicked, onRemoveFileRequestActionClicked, selectedFileRequest, trans]);



    const slideOutCompContent = useMemo(() => {
        return (
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <RCFileRequestInfoContainer
                        className="transparent"
                        projectId={props.projectId}
                        homeId={props.homeId}
                        deviceId={props.deviceId}
                        robotId={props.robotId}
                        selectedFileRequest={props.selectedFileRequest}
                        onCancel={() => {
                            uiDispatch(hideSlideOutPanel());
                        }}
                        onFileRequestCreated={() => {
                            uiDispatch(hideSlideOutPanel());
                        }}
                    />
                </Grid>
            </Grid>
        );
    }, [props.deviceId, props.homeId, props.projectId, props.robotId, props.selectedFileRequest, uiDispatch]);


    return (
        <SlideOutComp
            primaryActions={primaryActions}
            secondaryActions={secondaryActions}
            closable={!isNewFileRequest}
            compContent={slideOutCompContent}
            contentContainerProps={{
                className: 'slide-out-comp-container',
            }}
        />
    );
};
